/** @file Composites a packed sheet: copies tile images (or crops of a sheet image) to their places. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CellRect } from '@zilo/pose3d-export/types/bundle';
import type { Image } from '@zilo/pose3d-export/types/imageIoShapes';

/**
 * One image to place at `at`: the whole `image`, or its `from` rect (a sheet-backed tile cut from its sheet image);
 * `size` is the tile size it must have and `slot` names it in errors.
 */
export type Placement = { at: [number, number]; image: Image; from?: CellRect; size?: [number, number]; slot?: string };

/** Offsets `[first, end)` along one axis inside both the source (from `s`) and the destination (from `d`). */
const span = (s: number, d: number, n: number, srcSide: number, dstSide: number): [number, number] =>
  [Math.max(0, -s, -d), Math.min(n, srcSide - s, dstSide - d)];

function copy(dst: Image, p: Placement, [sx, sy, w, h]: CellRect): void {
  const [dx, dy] = p.at;
  const [x0, x1] = span(sx, dx, w, p.image.width, dst.width);
  const [y0, y1] = span(sy, dy, h, p.image.height, dst.height);
  if (x1 <= x0) return;
  for (let y = y0; y < y1; y += 1) {
    const s = ((sy + y) * p.image.width + sx + x0) * 4;
    dst.data.set(p.image.data.subarray(s, s + (x1 - x0) * 4), ((dy + y) * dst.width + dx + x0) * 4);
  }
}

/**
 * A `size` RGBA image, transparent where nothing is placed, with every placement copied (no blending, clipped to the
 * sheet). A placement whose image (or `from` rect) differs from its `size` throws `VALIDATION` naming the slot.
 */
export function compositeSheet(size: [number, number], placements: Placement[]): Image {
  const dst: Image = { width: size[0], height: size[1], data: new Uint8ClampedArray(size[0] * size[1] * 4) };
  for (const p of placements) {
    const rect: CellRect = p.from ?? [0, 0, p.image.width, p.image.height];
    if (p.size !== undefined && (rect[2] !== p.size[0] || rect[3] !== p.size[1])) {
      throwError(ERROR_CODES.VALIDATION, { slot: p.slot, expected: p.size, actual: [rect[2], rect[3]] }, {
        hint: `the image of slot ${p.slot ?? '?'} must be ${p.size[0]}x${p.size[1]} px` });
    }
    copy(dst, p, rect);
  }
  return dst;
}
